import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface LiveZelleCartRef {
  cartId: string;
  memoCode: string;
  /** Money has already arrived under this code; the rest is expected under it too. */
  partlyFunded: boolean;
}

interface Row {
  invoice_id: string;
  payment_carts: { id: string; memo_code: string | null; status: string; expires_at: string } | null;
}

/**
 * Invoices a live Zelle cart (bundle payment) covers, keyed by invoice id, so the
 * Payments list can point a family back at the code they were given. Informational:
 * an unfunded code does not stop the invoice being paid another way.
 */
export function useLiveZelleCarts(enabled: boolean): Map<string, LiveZelleCartRef> {
  const { data } = useQuery<Row[]>({
    queryKey: ['live-zelle-carts'],
    queryFn: async () => {
      try {
        const { data: rows, error } = await supabase
          .from('payment_cart_items')
          .select('invoice_id, payment_carts!inner(id, memo_code, status, expires_at)')
          .eq('payment_carts.kind', 'zelle')
          .in('payment_carts.status', ['open', 'partially_funded'])
          .gt('payment_carts.expires_at', new Date().toISOString());
        if (error) return [];
        return (rows ?? []) as unknown as Row[];
      } catch {
        return [];
      }
    },
    enabled,
    staleTime: 30 * 1000,
  });
  return useMemo(() => {
    const byInvoice = new Map<string, LiveZelleCartRef>();
    for (const row of Array.isArray(data) ? data : []) {
      const cart = row.payment_carts;
      if (!cart?.memo_code) continue;
      byInvoice.set(row.invoice_id, {
        cartId: cart.id,
        memoCode: cart.memo_code,
        partlyFunded: cart.status === 'partially_funded',
      });
    }
    return byInvoice;
  }, [data]);
}
